import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Loader2, Link2, Unlink, RefreshCw, ShoppingBag,
  Package, ArrowUpDown, CheckCircle2, XCircle, Clock, AlertTriangle,
  Upload, Download, Check, Square, CheckSquare
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface IFoodCredentials {
  id: string;
  restaurant_id: string;
  client_id: string;
  client_secret: string;
  merchant_id: string | null;
  is_active: boolean;
  last_sync_at: string | null;
  token_expires_at: string | null;
}

interface IFoodOrder {
  id: string;
  ifood_order_id: string;
  ifood_reference: string | null;
  status: string;
  ifood_status: string | null;
  customer_name: string | null;
  total: number;
  created_at: string;
}

interface LocalProduct {
  id: string;
  name: string;
  description: string | null;
  price: number;
  is_available: boolean;
  category_name?: string;
  ifood_mapped?: boolean;
  ifood_item_id?: string;
}

interface IFoodCatalogItem {
  ifood_item_id: string;
  name: string;
  description: string;
  price: number;
  category: string;
  image_url: string | null;
  status: string;
}

const IFoodIntegration = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [restaurantId, setRestaurantId] = useState<string | null>(null);
  const [credentials, setCredentials] = useState<IFoodCredentials | null>(null);
  const [orders, setOrders] = useState<IFoodOrder[]>([]);

  // Catalog state
  const [localProducts, setLocalProducts] = useState<LocalProduct[]>([]);
  const [ifoodItems, setIfoodItems] = useState<IFoodCatalogItem[]>([]);
  const [selectedPush, setSelectedPush] = useState<Set<string>>(new Set());
  const [selectedPull, setSelectedPull] = useState<Set<string>>(new Set());
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [catalogTab, setCatalogTab] = useState<"push" | "pull">("push");

  const [formClientId, setFormClientId] = useState("");
  const [formClientSecret, setFormClientSecret] = useState("");
  const [formMerchantId, setFormMerchantId] = useState("");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { navigate("/login"); return; }

    const impersonateId = sessionStorage.getItem("admin_impersonate_restaurant_id");
    let rest: any = null;
    
    if (impersonateId) {
      const { data } = await supabase
        .from("restaurants").select("id").eq("id", impersonateId).maybeSingle();
      rest = data;
    } else {
      const { data } = await supabase
        .from("restaurants").select("id").eq("owner_id", user.id).maybeSingle();
      rest = data;
    }
    
    if (!rest) { navigate("/dashboard"); return; }
    setRestaurantId(rest.id);

    const { data: creds } = await supabase
      .from("ifood_credentials" as any)
      .select("*")
      .eq("restaurant_id", rest.id)
      .maybeSingle();

    if (creds) {
      const c = creds as any as IFoodCredentials;
      setCredentials(c);
      setFormClientId(c.client_id);
      setFormClientSecret(c.client_secret);
      setFormMerchantId(c.merchant_id || "");
    }

    const { data: ifoodOrders } = await supabase
      .from("ifood_orders" as any)
      .select("*")
      .eq("restaurant_id", rest.id)
      .order("created_at", { ascending: false })
      .limit(50);

    if (ifoodOrders) setOrders(ifoodOrders as any as IFoodOrder[]);

    // Load local products with mapping info
    await loadLocalProducts(rest.id);

    setLoading(false);
  };

  const loadLocalProducts = async (restId: string) => {
    const { data: prods } = await supabase
      .from("products")
      .select("id, name, description, price, is_available, category_id, categories(name)")
      .eq("restaurant_id", restId);

    const { data: mappings } = await supabase
      .from("ifood_product_map" as any)
      .select("product_id, ifood_item_id")
      .eq("restaurant_id", restId);

    const mapLookup = new Map((mappings || []).map((m: any) => [m.product_id, m.ifood_item_id]));

    setLocalProducts(
      (prods || []).map((p: any) => ({
        id: p.id,
        name: p.name,
        description: p.description,
        price: p.price,
        is_available: p.is_available,
        category_name: p.categories?.name || "Sem categoria",
        ifood_mapped: mapLookup.has(p.id),
        ifood_item_id: mapLookup.get(p.id),
      }))
    );
  };

  const handleSaveCredentials = async () => {
    if (!restaurantId || !formClientId || !formClientSecret) {
      toast({ title: "Preencha Client ID e Client Secret", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      if (credentials) {
        const { error } = await supabase
          .from("ifood_credentials" as any)
          .update({
            client_id: formClientId,
            client_secret: formClientSecret,
            merchant_id: formMerchantId || null,
          } as any)
          .eq("id", credentials.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("ifood_credentials" as any)
          .insert({
            restaurant_id: restaurantId,
            client_id: formClientId,
            client_secret: formClientSecret,
            merchant_id: formMerchantId || null,
          } as any);
        if (error) throw error;
      }
      toast({ title: "Credenciais salvas com sucesso!" });
      await loadData();
    } catch (err: any) {
      toast({ title: "Erro ao salvar", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async () => {
    if (!credentials) return;
    try {
      const { error } = await supabase
        .from("ifood_credentials" as any)
        .update({ is_active: !credentials.is_active } as any)
        .eq("id", credentials.id);
      if (error) throw error;
      toast({ title: credentials.is_active ? "Integração desativada" : "Integração ativada" });
      await loadData();
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    }
  };

  const handleSyncOrders = async () => {
    if (!restaurantId) return;
    setSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke("ifood-sync-orders", {
        body: { restaurant_id: restaurantId },
      });
      if (error) throw error;
      toast({ title: "Pedidos sincronizados!", description: `${data?.synced || 0} novos pedidos importados` });
      await loadData();
    } catch (err: any) {
      toast({ title: "Erro na sincronização", description: err.message, variant: "destructive" });
    } finally {
      setSyncing(false);
    }
  };

  // Catalog functions
  const handleFetchIfoodCatalog = async () => {
    if (!restaurantId) return;
    setCatalogLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("ifood-sync-catalog", {
        body: { restaurant_id: restaurantId, action: "fetch_catalog" },
      });
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || "Erro desconhecido");
      setIfoodItems(data.items || []);
      toast({ title: "Catálogo carregado!", description: `${data.total} itens encontrados no iFood` });
    } catch (err: any) {
      toast({ title: "Erro ao buscar catálogo", description: err.message, variant: "destructive" });
    } finally {
      setCatalogLoading(false);
    }
  };

  const handlePushProducts = async () => {
    if (!restaurantId || selectedPush.size === 0) return;
    setPushing(true);
    try {
      const productsToSend = localProducts
        .filter((p) => selectedPush.has(p.id))
        .map((p) => ({
          id: p.id,
          name: p.name,
          description: p.description,
          price: p.price,
          is_available: p.is_available,
        }));

      const { data, error } = await supabase.functions.invoke("ifood-sync-catalog", {
        body: { restaurant_id: restaurantId, action: "push_products", products: productsToSend },
      });
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || "Erro desconhecido");

      toast({
        title: "Produtos enviados ao iFood!",
        description: `${data.pushed} produtos sincronizados${data.errors?.length ? `, ${data.errors.length} erros` : ""}`,
      });
      setSelectedPush(new Set());
      await loadLocalProducts(restaurantId);
    } catch (err: any) {
      toast({ title: "Erro ao enviar", description: err.message, variant: "destructive" });
    } finally {
      setPushing(false);
    }
  };

  const handlePullProducts = async () => {
    if (!restaurantId || selectedPull.size === 0) return;
    setPulling(true);
    try {
      const itemsToImport = ifoodItems
        .filter((i) => selectedPull.has(i.ifood_item_id))
        .map((i) => ({
          ifood_item_id: i.ifood_item_id,
          name: i.name,
          description: i.description,
          price: i.price,
        }));

      const { data, error } = await supabase.functions.invoke("ifood-sync-catalog", {
        body: { restaurant_id: restaurantId, action: "pull_products", products: itemsToImport },
      });
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || "Erro desconhecido");

      toast({
        title: "Produtos importados!",
        description: `${data.imported} produtos importados do iFood${data.errors?.length ? `, ${data.errors.length} erros` : ""}`,
      });
      setSelectedPull(new Set());
      await loadLocalProducts(restaurantId);
    } catch (err: any) {
      toast({ title: "Erro ao importar", description: err.message, variant: "destructive" });
    } finally {
      setPulling(false);
    }
  };

  const togglePush = (id: string) => {
    setSelectedPush((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const togglePull = (id: string) => {
    setSelectedPull((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAllPush = () => {
    if (selectedPush.size === localProducts.length) {
      setSelectedPush(new Set());
    } else {
      setSelectedPush(new Set(localProducts.map((p) => p.id)));
    }
  };

  const toggleAllPull = () => {
    if (selectedPull.size === ifoodItems.length) {
      setSelectedPull(new Set());
    } else {
      setSelectedPull(new Set(ifoodItems.map((i) => i.ifood_item_id)));
    }
  };

  const getStatusBadge = (status: string) => {
    const map: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
      received: { label: "Recebido", variant: "default" },
      preparing: { label: "Preparando", variant: "secondary" },
      ready: { label: "Pronto", variant: "outline" },
      dispatched: { label: "Saiu p/ Entrega", variant: "secondary" },
      completed: { label: "Concluído", variant: "default" },
      cancelled: { label: "Cancelado", variant: "destructive" },
    };
    const info = map[status] || { label: status, variant: "outline" as const };
    return <Badge variant={info.variant}>{info.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-border bg-card/95 backdrop-blur">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-destructive/10 flex items-center justify-center">
              <ShoppingBag className="h-4 w-4 text-destructive" />
            </div>
            <h1 className="font-display font-bold text-lg">Integração iFood</h1>
          </div>
          {credentials && (
            <Badge variant={credentials.is_active ? "default" : "secondary"} className="ml-auto">
              {credentials.is_active ? "Ativo" : "Inativo"}
            </Badge>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        <Tabs defaultValue="config" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="config" className="gap-2">
              <Link2 className="h-4 w-4" /> Configuração
            </TabsTrigger>
            <TabsTrigger value="orders" className="gap-2">
              <Package className="h-4 w-4" /> Pedidos iFood
            </TabsTrigger>
            <TabsTrigger value="catalog" className="gap-2">
              <ArrowUpDown className="h-4 w-4" /> Cardápio
            </TabsTrigger>
          </TabsList>

          {/* Tab: Configuração */}
          <TabsContent value="config" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Credenciais iFood</CardTitle>
                <CardDescription>
                  Para conectar, acesse o{" "}
                  <a
                    href="https://developer.ifood.com.br"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline"
                  >
                    Portal do Desenvolvedor iFood
                  </a>{" "}
                  e crie um aplicativo para obter seu Client ID e Client Secret.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Client ID *</Label>
                    <Input
                      value={formClientId}
                      onChange={(e) => setFormClientId(e.target.value)}
                      placeholder="Seu Client ID do iFood"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Client Secret *</Label>
                    <Input
                      type="password"
                      value={formClientSecret}
                      onChange={(e) => setFormClientSecret(e.target.value)}
                      placeholder="Seu Client Secret do iFood"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Merchant ID (Código da Loja)</Label>
                  <Input
                    value={formMerchantId}
                    onChange={(e) => setFormMerchantId(e.target.value)}
                    placeholder="ID da sua loja no iFood"
                  />
                </div>

                <div className="flex items-center justify-between pt-2">
                  <div className="flex items-center gap-3">
                    <Button onClick={handleSaveCredentials} disabled={saving} className="gap-2">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                      Salvar Credenciais
                    </Button>
                    {credentials && (
                      <div className="flex items-center gap-2">
                        <Switch checked={credentials.is_active} onCheckedChange={handleToggleActive} />
                        <span className="text-sm text-muted-foreground">
                          {credentials.is_active ? "Integração ativa" : "Integração inativa"}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {credentials && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Status da Conexão</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50">
                      {credentials.is_active ? (
                        <CheckCircle2 className="h-5 w-5 text-primary" />
                      ) : (
                        <XCircle className="h-5 w-5 text-destructive" />
                      )}
                      <div>
                        <p className="text-sm font-medium">Conexão</p>
                        <p className="text-xs text-muted-foreground">
                          {credentials.is_active ? "Conectado" : "Desconectado"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50">
                      <Clock className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">Última Sincronização</p>
                        <p className="text-xs text-muted-foreground">
                          {credentials.last_sync_at
                            ? new Date(credentials.last_sync_at).toLocaleString("pt-BR")
                            : "Nunca"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50">
                      <AlertTriangle className="h-5 w-5 text-amber-500" />
                      <div>
                        <p className="text-sm font-medium">Token</p>
                        <p className="text-xs text-muted-foreground">
                          {credentials.token_expires_at
                            ? new Date(credentials.token_expires_at) > new Date()
                              ? "Válido"
                              : "Expirado"
                            : "Não autenticado"}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Tab: Pedidos */}
          <TabsContent value="orders" className="space-y-4 mt-6">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold">Pedidos Recentes do iFood</h2>
              <Button variant="outline" onClick={handleSyncOrders} disabled={syncing || !credentials?.is_active} className="gap-2">
                {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                Sincronizar
              </Button>
            </div>

            {!credentials?.is_active && (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  <Unlink className="h-10 w-10 mx-auto mb-3 opacity-40" />
                  <p>Ative a integração na aba Configuração para receber pedidos.</p>
                </CardContent>
              </Card>
            )}

            {credentials?.is_active && orders.length === 0 && (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  <Package className="h-10 w-10 mx-auto mb-3 opacity-40" />
                  <p>Nenhum pedido do iFood ainda. Clique em Sincronizar para buscar.</p>
                </CardContent>
              </Card>
            )}

            {orders.length > 0 && (
              <div className="space-y-2">
                {orders.map((order) => (
                  <Card key={order.id} className="hover:border-primary/20 transition-colors">
                    <CardContent className="py-3 px-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center shrink-0">
                          <ShoppingBag className="h-5 w-5 text-destructive" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">
                            #{order.ifood_reference || order.ifood_order_id.slice(0, 8)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {order.customer_name || "Cliente"} •{" "}
                            {new Date(order.created_at).toLocaleString("pt-BR")}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-semibold text-sm">
                          R$ {Number(order.total).toFixed(2)}
                        </span>
                        {getStatusBadge(order.status)}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Tab: Cardápio */}
          <TabsContent value="catalog" className="space-y-4 mt-6">
            {!credentials?.is_active ? (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  <Unlink className="h-10 w-10 mx-auto mb-3 opacity-40" />
                  <p>Ative a integração na aba Configuração para sincronizar o cardápio.</p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Direction toggle */}
                <div className="flex gap-2">
                  <Button
                    variant={catalogTab === "push" ? "default" : "outline"}
                    onClick={() => setCatalogTab("push")}
                    className="gap-2"
                  >
                    <Upload className="h-4 w-4" />
                    Enviar para iFood
                  </Button>
                  <Button
                    variant={catalogTab === "pull" ? "default" : "outline"}
                    onClick={() => {
                      setCatalogTab("pull");
                      if (ifoodItems.length === 0) handleFetchIfoodCatalog();
                    }}
                    className="gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Importar do iFood
                  </Button>
                </div>

                {/* PUSH: ViaMenu → iFood */}
                {catalogTab === "push" && (
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-lg">Enviar Produtos para o iFood</CardTitle>
                          <CardDescription>
                            Selecione os produtos do ViaMenu que deseja publicar no iFood.
                          </CardDescription>
                        </div>
                        <Button
                          onClick={handlePushProducts}
                          disabled={pushing || selectedPush.size === 0}
                          className="gap-2"
                        >
                          {pushing ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Upload className="h-4 w-4" />
                          )}
                          Enviar ({selectedPush.size})
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {localProducts.length === 0 ? (
                        <p className="text-center text-muted-foreground py-6">
                          Nenhum produto cadastrado. Crie produtos no cardápio primeiro.
                        </p>
                      ) : (
                        <div className="space-y-1">
                          {/* Select all */}
                          <button
                            onClick={toggleAllPush}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-secondary/50 transition-colors text-sm text-muted-foreground"
                          >
                            {selectedPush.size === localProducts.length ? (
                              <CheckSquare className="h-4 w-4 text-primary" />
                            ) : (
                              <Square className="h-4 w-4" />
                            )}
                            Selecionar todos ({localProducts.length})
                          </button>

                          {localProducts.map((p) => (
                            <button
                              key={p.id}
                              onClick={() => togglePush(p.id)}
                              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-secondary/50 transition-colors text-left"
                            >
                              {selectedPush.has(p.id) ? (
                                <CheckSquare className="h-4 w-4 text-primary shrink-0" />
                              ) : (
                                <Square className="h-4 w-4 text-muted-foreground shrink-0" />
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-sm truncate">{p.name}</span>
                                  {p.ifood_mapped && (
                                    <Badge variant="outline" className="text-xs shrink-0">
                                      <Check className="h-3 w-3 mr-1" /> Sincronizado
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground">
                                  {p.category_name} • R$ {p.price.toFixed(2)}
                                </p>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* PULL: iFood → ViaMenu */}
                {catalogTab === "pull" && (
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-lg">Importar Itens do iFood</CardTitle>
                          <CardDescription>
                            Itens do catálogo iFood. Selecione para importar como produtos no ViaMenu.
                          </CardDescription>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            onClick={handleFetchIfoodCatalog}
                            disabled={catalogLoading}
                            className="gap-2"
                          >
                            {catalogLoading ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <RefreshCw className="h-4 w-4" />
                            )}
                            Atualizar
                          </Button>
                          <Button
                            onClick={handlePullProducts}
                            disabled={pulling || selectedPull.size === 0}
                            className="gap-2"
                          >
                            {pulling ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Download className="h-4 w-4" />
                            )}
                            Importar ({selectedPull.size})
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {catalogLoading ? (
                        <div className="flex items-center justify-center py-10">
                          <Loader2 className="h-6 w-6 animate-spin text-primary" />
                          <span className="ml-3 text-muted-foreground">Carregando catálogo do iFood...</span>
                        </div>
                      ) : ifoodItems.length === 0 ? (
                        <div className="text-center text-muted-foreground py-8">
                          <ArrowUpDown className="h-10 w-10 mx-auto mb-3 opacity-40" />
                          <p>Nenhum item encontrado no catálogo iFood.</p>
                          <p className="text-xs mt-1">
                            Verifique se o Merchant ID está correto e se há itens cadastrados no iFood.
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          {/* Select all */}
                          <button
                            onClick={toggleAllPull}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-secondary/50 transition-colors text-sm text-muted-foreground"
                          >
                            {selectedPull.size === ifoodItems.length ? (
                              <CheckSquare className="h-4 w-4 text-primary" />
                            ) : (
                              <Square className="h-4 w-4" />
                            )}
                            Selecionar todos ({ifoodItems.length})
                          </button>

                          {ifoodItems.map((item) => (
                            <button
                              key={item.ifood_item_id}
                              onClick={() => togglePull(item.ifood_item_id)}
                              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-secondary/50 transition-colors text-left"
                            >
                              {selectedPull.has(item.ifood_item_id) ? (
                                <CheckSquare className="h-4 w-4 text-primary shrink-0" />
                              ) : (
                                <Square className="h-4 w-4 text-muted-foreground shrink-0" />
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-sm truncate">{item.name}</span>
                                  <Badge variant="outline" className="text-xs shrink-0">
                                    {item.category}
                                  </Badge>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                  R$ {item.price.toFixed(2)}
                                  {item.description && ` • ${item.description.slice(0, 60)}...`}
                                </p>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default IFoodIntegration;
